import React from 'react';
import {
  Calendar,
  Clock,
  MapPin,
  User,
  Phone,
  Mail,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  Building,
  Utensils,
  Navigation,
  Check,
  X
} from 'lucide-react';
import ProfilePicture from './ProfilePicture';

const EnhancedMeetingCard = ({
  meeting,
  onClick,
  userType,
  showDetails = true,
  compact = false,
  onAccept,
  onDecline,
  showRSVP = false
}) => {
  // Helper function to parse date strings as local dates (avoiding timezone issues)
  const parseLocalDate = (dateString) => {
    if (dateString && dateString.includes('-') && dateString.split('-').length === 3) {
      const [year, month, day] = dateString.split('-');
      return new Date(year, month - 1, day); // Month is 0-indexed
    } else {
      return new Date(dateString);
    }
  };

  const formatDate = (dateStr) => {
    return parseLocalDate(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return 'Time TBD';

    // Handle both "HH:MM" and "HH:MM:SS" formats
    const timeParts = timeStr.split(':');
    const hours = parseInt(timeParts[0], 10);
    const minutes = parseInt(timeParts[1], 10);

    // Check for invalid time
    if (isNaN(hours) || isNaN(minutes)) {
      console.error('Invalid time format:', timeStr);
      return 'Time TBD';
    }

    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${displayHour}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  const getStatusColor = (status) => {
    switch(status?.toLowerCase()) {
      case 'confirmed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch(status?.toLowerCase()) {
      case 'confirmed':
        return <CheckCircle className="w-4 h-4" />;
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'cancelled':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getRSVPBadge = (rsvpStatus) => {
    switch(rsvpStatus?.toLowerCase()) {
      case 'accepted':
        return (
          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Accepted
          </span>
        );
      case 'declined':
        return (
          <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full flex items-center gap-1">
            <X className="w-3 h-3" />
            Declined
          </span>
        );
      case 'maybe':
        return (
          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Maybe
          </span>
        );
      default:
        return null;
    }
  };

  // Get current date for comparisons (normalized to midnight)
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const meetingDate = parseLocalDate(meeting.date);
  meetingDate.setHours(0, 0, 0, 0);

  const isToday = meetingDate.getTime() === today.getTime();
  const isTomorrow = meetingDate.getTime() === (today.getTime() + 24 * 60 * 60 * 1000);
  const isUrgent = isToday || isTomorrow;

  if (compact) {
    return (
      <div 
        onClick={onClick}
        className={`p-4 rounded-lg border cursor-pointer transition-all duration-200 hover:shadow-md ${
          isToday ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`w-3 h-3 rounded-full ${
              meeting.status === 'confirmed' ? 'bg-green-400' : 
              meeting.status === 'pending' ? 'bg-yellow-400' : 
              'bg-gray-400'
            }`}></div>
            <div>
              <p className="font-medium text-sm text-gray-800">
                {userType === 'sales' ? meeting.customer : (meeting.salesRep || 'Sales Representative')}
              </p>
              <p className="text-xs text-gray-600">
                {formatDate(meeting.date)} • {formatTime(meeting.time)}
              </p>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden ${
        isToday ? 'ring-2 ring-blue-600' : ''
      }`}
    >
      {/* Header with status and urgency indicators */}
      <div className="p-3 sm:p-4 pb-2 sm:pb-3">
        <div className="flex items-start justify-between gap-2 mb-2 sm:mb-3">
          <div className="flex items-start space-x-2 sm:space-x-3 flex-1 min-w-0">
            <ProfilePicture
              src={userType === 'sales' ? meeting.customer_profile_picture : meeting.sales_rep_profile_picture}
              name={userType === 'sales' ? meeting.customer : meeting.salesRep}
              size="lg"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1 flex-wrap">
                <h4 className="font-semibold text-gray-800 truncate">
                  {userType === 'sales' ? meeting.customer : (meeting.salesRep || 'Sales Representative')}
                </h4>
                {isUrgent && (
                  <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-full whitespace-nowrap flex-shrink-0">
                    {isToday ? 'Today' : isTomorrow ? 'Tomorrow' : 'Soon'}
                  </span>
                )}
              </div>
              {meeting.company && (
                <div className="flex items-center space-x-1 text-sm text-gray-600 min-w-0">
                  <Building className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate">{meeting.company}</span>
                </div>
              )}
            </div>
          </div>
          <div className={`px-2 py-1 rounded-full text-xs font-medium border flex items-center space-x-1 flex-shrink-0 ${getStatusColor(meeting.status)}`}>
            {getStatusIcon(meeting.status)}
            <span className="capitalize hidden sm:inline">{meeting.status || 'pending'}</span>
          </div>
        </div>

        {/* Meeting Details - tighter spacing on mobile */}
        <div className="space-y-0.5 sm:space-y-1.5 mb-1">
          <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 text-sm text-gray-600">
            <div className="flex items-center space-x-1">
              <Calendar className="w-4 h-4 text-blue-500 flex-shrink-0" />
              <span className="font-bold">{formatDate(meeting.date)}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Clock className="w-4 h-4 text-green-500 flex-shrink-0" />
              <span>{formatTime(meeting.time)}</span>
            </div>
          </div>

          {meeting.location_name && meeting.location_address && (
            <a
              href={`https://maps.apple.com/?q=${encodeURIComponent(meeting.location_address)}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center space-x-1.5 text-sm text-gray-600 hover:text-blue-600 hover:underline min-w-0 transition-colors"
            >
              <Navigation className="w-4 h-4 text-blue-500 flex-shrink-0" />
              <span className="truncate">{meeting.location_name}</span>
            </a>
          )}

          {meeting.restaurant && (
            <a
              href={`https://maps.apple.com/?q=${encodeURIComponent(meeting.restaurant + (meeting.location ? ' ' + meeting.location : ''))}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center space-x-1.5 text-sm text-gray-600 hover:text-gray-800 hover:underline min-w-0"
            >
              <Utensils className="w-4 h-4 text-orange-500 flex-shrink-0" />
              <span className="truncate">{meeting.restaurant}</span>
            </a>
          )}

          {meeting.restaurant_phone && (
            <a
              href={`tel:${meeting.restaurant_phone.replace(/\D/g, '')}`}
              onClick={(e) => e.stopPropagation()}
              className="flex items-center space-x-1.5 text-sm text-blue-600 hover:text-blue-800 hover:underline min-w-0"
            >
              <Phone className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">{meeting.restaurant_phone}</span>
            </a>
          )}
        </div>

        {/* Contact Information */}
        {showDetails && (meeting.email || meeting.phone) && (
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-xs text-gray-500 py-2 px-3 bg-gray-50 rounded-lg">
            {meeting.email && (
              <div className="flex items-center space-x-1 min-w-0">
                <Mail className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{meeting.email}</span>
              </div>
            )}
            {meeting.phone && (
              <div className="flex items-center space-x-1 flex-shrink-0">
                <Phone className="w-3 h-3" />
                <span>{meeting.phone}</span>
              </div>
            )}
          </div>
        )}

        {/* RSVP Section */}
        {showRSVP && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            {meeting.user_rsvp_status === 'pending' && !meeting.user_is_organizer ? (
              // Show RSVP buttons for pending invitations
              <div>
                <p className="text-sm text-gray-700 mb-2 font-medium">
                  You've been invited to this meeting
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onAccept) onAccept(meeting);
                    }}
                    className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <Check className="w-4 h-4" />
                    Accept
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onDecline) onDecline(meeting);
                    }}
                    className="flex-1 px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Decline
                  </button>
                </div>
              </div>
            ) : (
              // Show RSVP status for already-responded invitations
              meeting.user_rsvp_status && meeting.user_rsvp_status !== 'pending' && !meeting.user_is_organizer && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">Your RSVP:</p>
                  {getRSVPBadge(meeting.user_rsvp_status)}
                </div>
              )
            )}
          </div>
        )}
      </div>

      {/* Footer with additional info */}
      {showDetails && (
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600">
              {meeting.attendees && (
                <span className="whitespace-nowrap">{meeting.attendees} attendee{meeting.attendees !== 1 ? 's' : ''}</span>
              )}
              {userType === 'sales' && meeting.orderTotal && (
                <span>Cost: ${meeting.orderTotal}</span>
              )}
              {userType === 'customer' && meeting.products && (
                <span className="whitespace-nowrap">{meeting.products.length} product{meeting.products.length !== 1 ? 's' : ''}</span>
              )}
            </div>
            <div className="flex items-center space-x-1 text-xs text-blue-600 flex-shrink-0">
              <span>Details</span>
              <ChevronRight className="w-3 h-3" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedMeetingCard;